/**
 * Confirms deleting a contact (#332).
 *
 * The server deletes the contact and every message in its thread, and keeps
 * its leads with the contact link cleared — the copy says exactly that. A
 * failed delete keeps the dialog open with the server's message.
 */

import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useDeleteContact } from "@/features/realtor/hooks/use-realtor-crm";
import type { ContactSummary } from "@/features/realtor/services/realtor-crm-api.service";
import { StudioButton } from "@/features/studio/theme";

interface DeleteContactDialogProps {
	summary: ContactSummary;
	onClose: () => void;
}

export function DeleteContactDialog({
	summary,
	onClose,
}: DeleteContactDialogProps) {
	const { t } = useTranslation();
	const deleteContact = useDeleteContact();
	const { contact, leadCount } = summary;

	const handleConfirm = () => {
		if (deleteContact.isPending) return;
		deleteContact.mutate(contact.id, {
			onSuccess: () => {
				toast.success(t("realtor.contacts.delete.deletedToast"));
				onClose();
			},
		});
	};

	return (
		<Dialog
			open
			onOpenChange={(open) => !open && !deleteContact.isPending && onClose()}
		>
			<DialogContent className="w-[calc(100%-2rem)] rounded-xl bg-dashboard-bg border-dashboard-border sm:w-full">
				<DialogHeader>
					<DialogTitle className="font-display text-xl italic font-medium text-dashboard-text">
						{t("realtor.contacts.delete.title", { name: contact.name })}
					</DialogTitle>
					<DialogDescription className="text-dashboard-muted">
						{t("realtor.contacts.delete.body", { name: contact.name })}
						{leadCount > 0 && (
							<>
								{" "}
								{t("realtor.contacts.delete.leadsKept", { count: leadCount })}
							</>
						)}
					</DialogDescription>
				</DialogHeader>

				{deleteContact.error && (
					<p
						role="alert"
						className="rounded-md px-3 py-2 text-sm"
						style={{
							color: "var(--color-dashboard-pill-risk)",
							backgroundColor: "var(--color-dashboard-pill-risk-bg)",
						}}
					>
						{deleteContact.error.message}
					</p>
				)}

				<DialogFooter className="gap-2">
					<StudioButton
						variant="quiet"
						size="sm"
						onClick={onClose}
						disabled={deleteContact.isPending}
					>
						{t("realtor.contacts.delete.cancel")}
					</StudioButton>
					<StudioButton
						size="sm"
						className="gap-1.5"
						onClick={handleConfirm}
						disabled={deleteContact.isPending}
						style={{
							background: "var(--color-dashboard-danger-fill)",
							boxShadow: "none",
						}}
					>
						{deleteContact.isPending && (
							<Loader2 className="h-4 w-4 animate-spin" />
						)}
						{deleteContact.isPending
							? t("realtor.contacts.delete.deleting")
							: t("realtor.contacts.delete.confirm")}
					</StudioButton>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
